import React, { useEffect, useState } from "react";
import { Stripe, StripeError } from "@stripe/stripe-js";

import {useActionApi, usePersistedState} from "hooks";
import { entities } from "reduxStore";
import Api, { config } from "api";
import { navigateToCordovaLocationsScreen } from 'utils'
import { getStripe } from "./utils";
import { Token, SavedCard, StripeCard } from "./types";

import Popup from "components/Popup";
import { Location } from "components/BillingLocationSelector";

import { Tokens } from './popups/Tokens'
import { Checkout } from './popups/Checkout'
import { Confirmation } from './popups/Confirmation'
import { Failure } from './popups/Failure'
import {UserAccountProps} from "utils/types";

// @ts-ignore
const app = window.app as any;
// @ts-ignore
const TempStars = window.TempStars;

interface PurchaseTokensFlowProps {
    onClose: () => void,
    onSuccess: () => void
}

enum PopupType {
    tokens = 'tokens',
    checkout = 'checkout',
    confirmation = 'confirmation',
    failure = 'failure'
}

const PurchaseTokensFlow = ({ onClose, onSuccess }: PurchaseTokensFlowProps) => {
    const [stripe, setStripe] = useState<Stripe | null>(null);

    useEffect(() => {
        getStripe(country).then(stripe => setStripe(stripe))
    }, []);

    const [currentPopup, setCurrentPopup] = useState<PopupType>(PopupType.tokens);
    const [selectedToken, setSelectedToken] = useState<Token | null>(null);
    const [selectedBillingLocation, setSelectedBillingLocation] = useState<Location | null>(null);

    const [billingCardError, setBillingCardError] = useState('');
    const [savedBillingCard, setSavedBillingCard] = useState<SavedCard | null>(null);
    const [updatedBillingStripeCard, setUpdatedBillingStripeCard] = useState<StripeCard | null>(null);
    const [isBillingCardLoading, setIsBillingCardLoading] = useState<boolean>(false);

    const dentistUser = usePersistedState({
        entityName: entities.user
    })[0] as UserAccountProps

    const countryCode = usePersistedState({
        entityName: entities.countryCode,
    })[0]

    const billingLocations = usePersistedState({
        entityName: entities.dentistBillingLocations,
        initialValue: [],
    })[0] as Location[]

    const [purchaseTokensLoading, purchaseTokensError, purchaseTokens] = useActionApi({
        apiMethod: Api.purchaseTokens,
        onSuccess: () => {
            setBillingCardError('');
            app.hidePreloader();
            setCurrentPopup(PopupType.confirmation);
        },
        onError: () => {
            setBillingCardError('');
            app.hidePreloader();
            setCurrentPopup(PopupType.failure);
        },
    })

    const onBillingLocationSelect = async (location: Location) => {
        setSelectedBillingLocation(location);

        const savedLocationCard = await Api.cardById(location?.stripeCustomerId, location?.country);
        setSavedBillingCard(savedLocationCard || null)
        setUpdatedBillingStripeCard(null)
    }

    useEffect(() => {
        if (!selectedBillingLocation && billingLocations.length) {
            onBillingLocationSelect(billingLocations[0])
        }
    }, [billingLocations, selectedBillingLocation])

    const country = dentistUser.dentist?.countryStr || countryCode as string;

    const saveUpdateBillingStripeCard = async () => {
        const updateErrorMessage = (error: StripeError) => {
            if (error.message && (error.type === "card_error" || error.type === "validation_error")) {
                setBillingCardError(error.message);
            } else {
                setBillingCardError("An unexpected error occurred.");
            }
        }

        try {
            if (!stripe || !selectedBillingLocation || !updatedBillingStripeCard) {
                return;
            }
            setIsBillingCardLoading(true);

            const intent = await Api.createSetupIntent(selectedBillingLocation.id);
            const { error } = await stripe.confirmCardSetup(intent?.client_secret, {
                payment_method: {
                    card: updatedBillingStripeCard
                }
            });


            if (error) {
                setIsBillingCardLoading(false);
                updateErrorMessage(error as any);
                return false
            }

            await Api.updatePaymentInfo(selectedBillingLocation.id);
            await TempStars.User.refresh();
            setIsBillingCardLoading(false);

            return true;
        } catch (error: any) {
            updateErrorMessage(error as any);
            setIsBillingCardLoading(false);
            return false;
        }
    }

    const onCheckoutSubmit = async () => {
        app.showPreloader('Purchasing Tokens');

        const creditCardIsValid = !savedBillingCard && updatedBillingStripeCard
            ? await saveUpdateBillingStripeCard()
            : true;

        if (!creditCardIsValid || !selectedBillingLocation || !selectedToken) {
            app.hidePreloader();
            return;
        }

        purchaseTokens({
            locationId: selectedBillingLocation.id,
            tokensAmount: selectedToken.count,
        })
    }

    return <>
        <Popup opened={currentPopup === PopupType.tokens}>
            <Tokens
                onCloseButtonClick={onClose}
                onTokenClick={(token: Token) => {
                    setSelectedToken(token)
                    setCurrentPopup(PopupType.checkout)
                }}
                country={country}
                infoLink={
                    country === 'US'
                        ? config.US.tokensLink
                        : config.CA.tokensLink
                    }
            />
        </Popup>
        <Popup opened={currentPopup === PopupType.checkout}>
            {selectedToken ? (
                <Checkout
                    stripe={stripe}
                    onSubmit={onCheckoutSubmit}
                    savedBillingCard={savedBillingCard}
                    setSavedBillingCard={setSavedBillingCard}
                    updatedBillingStripeCard={updatedBillingStripeCard}
                    setUpdatedBillingStripeCard={setUpdatedBillingStripeCard}
                    locations={billingLocations}
                    onSelectedLocationChange={onBillingLocationSelect}
                    selectedLocation={selectedBillingLocation}
                    country={country}
                    onCloseButtonClick={onClose}
                    selectedToken={selectedToken}
                    onTokenSelect={setSelectedToken}
                    disabled={purchaseTokensLoading || isBillingCardLoading}
                    errorMessage={billingCardError}
                    onAddLocationClick={() => {
                        navigateToCordovaLocationsScreen();
                    }}
                />
            ) : null}
        </Popup>
        <Popup opened={currentPopup === PopupType.confirmation}>
            {selectedToken ? (
                <Confirmation
                    onConfirm={onSuccess}
                    selectedToken={selectedToken}
                />
            ) : null}
        </Popup>
        <Popup opened={currentPopup === PopupType.failure}>
            <Failure
                onClose={onClose}
                onConfirm={() => {
                    setSavedBillingCard(null);
                    setBillingCardError('Please update payment info.')
                    setCurrentPopup(PopupType.checkout);
                }}
            />
        </Popup>
    </>;
}

export default PurchaseTokensFlow;
